import _ from 'lodash'
import { useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { StructureActivityCode } from '@/core/models/establishment.model'
import { useGetAdminEstablishmentQuery, useUpdateAdminEstablishmentMutation } from '@/core/services/admin'

import { EstablishmentForm, EstablishmentFormData } from '@/features/establishment'

import { getApiErrorMessage } from '@/utils/errors'
import { Button, Paper, Stack } from '@mui/material'

export function EditEstablishment() {
  const formId = 'edit-establishment'

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: establishment } = useGetAdminEstablishmentQuery()
  const [updateEstablishment] = useUpdateAdminEstablishmentMutation()

  const initialValues = useMemo(() => {
    if (!establishment) return
    return {
      ..._.pick(establishment, [
        'name',
        'finess',
        'address',
        'zipCode',
        'city',
        'country',
        'phone',
        'preferredLanguage',
        'ordoclicVidalLicense',
        'desmosConfig',
        'isJoinableByPatients',
      ]),
      structureActivity: _.get(establishment, 'structureActivity.code', ''),
      ordoclicVidalLicense: _.get(establishment, 'ordoclicVidalLicense', { id: '', secret: '' }),
      desmosConfig: _.get(establishment, 'desmosConfig', { apiUrl: '', identityApiUrl: '', login: '', password: '', finess: '' }),
    }
  }, [establishment])

  const handleSubmit = async (data: EstablishmentFormData) => {
    if (!establishment) return

    try {
      await updateEstablishment({
        establishmentId: establishment._id,
        name: data.name,
        finess: data.finess,
        address: data.address,
        zipCode: data.zipCode,
        city: data.city,
        country: data.country,
        phone: data.phone,
        preferredLanguage: data.preferredLanguage || null,
        isJoinableByPatients: data.isJoinableByPatients,
        structureActivity: data.structureActivity
          ? {
            code: data.structureActivity as StructureActivityCode,
          }
          : undefined,
        ordoclicVidalLicense:
          data.ordoclicVidalLicense?.id && data.ordoclicVidalLicense?.secret
            ? {
              id: data.ordoclicVidalLicense.id,
              secret: data.ordoclicVidalLicense.secret,
            }
            : undefined,
        desmosConfig:
          data.desmosConfig?.apiUrl || data.desmosConfig?.identityApiUrl || data.desmosConfig?.login || data.desmosConfig?.password
            ? {
              apiUrl: data.desmosConfig.apiUrl || '',
              identityApiUrl: data.desmosConfig.identityApiUrl || '',
              login: data.desmosConfig.login || '',
              password: data.desmosConfig.password || '',
              finess: data.desmosConfig.finess || '',
            }
            : undefined,
      }).unwrap()

      enqueueSnackbar(t('notification.establishment.update.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.establishment.update.error')), { variant: 'error' })
    }
  }
  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <EstablishmentForm id={formId} initialValues={initialValues} onSubmit={handleSubmit} />
        <div>
          <Button form={formId} variant="contained" type="submit">
            {t('common:button.submit.label')}
          </Button>
        </div>
      </Stack>
    </Paper>
  )
}
